import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectHeaderPartnerCurrencies } from 'store/slices/header';
import {
    selectUserActivePartner,
    selectUserApplicationModes,
    selectUserData,
    setUserDataApplicationModes,
} from 'store/slices/userData';
// Import UI Components
import { Modal, Dropdown, Button, Radio, BusyLoader } from '@geneui/components';
// Import Components
import ButtonWithLoader from 'components/common/ButtonWithLoader';
// Import Services
import { AuthHttpService } from 'services/http';
// Import Hooks
import { useGTMEvent } from 'hooks/UseGTM';
// Import Constants
import { l, Appearance, ViewMode, GACategoryEvents } from 'constants/common';
// Import Images
import darkModeImg from 'assets/images/dark-mode-image.svg';
import lightModeImg from 'assets/images/light-mode-image.svg';

const { GAUserSettingsCategory } = GACategoryEvents;

const UserProfileSettingsModal = ({ closeModal, saveHandler }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { logEvent } = useGTMEvent();

    const partnerCurrencies = useAppSelector(selectHeaderPartnerCurrencies);
    const userData = useAppSelector(selectUserData);
    const userApplicationModes = useAppSelector(selectUserApplicationModes);
    const ActivePartner = useAppSelector(selectUserActivePartner);

    const [isSaveButtonLoading, setIsSaveButtonLoading] = useState(false);

    const [selectedPartnerCurrency, setSelectedPartnerCurrency] = useState(userData?.Settings.CurrencyId);
    const [initialPartnerCurrency, setInitialPartnerCurrency] = useState(userData?.Settings.CurrencyId);

    const [viewMode, setViewMode] = useState<number>(userApplicationModes.ViewMode ?? ViewMode.Normal);
    const [viewTheme, setViewTheme] = useState<number>(userApplicationModes.Appearance ?? Appearance.Light);
    const [isLoading, setIsLoading] = useState(false);

    const initialModeValues = useRef({
        ViewMode: viewMode,
        Appearance: viewTheme,
    });

    const partnerCurrencyRef = useRef(null);

    const isPartnerCurrencyChanged = initialPartnerCurrency !== selectedPartnerCurrency;
    const isViewModeChanged = +viewMode !== +initialModeValues.current.ViewMode;
    const isViewThemeChanged = +viewTheme !== +initialModeValues.current.Appearance;

    const partnerCurrencyChangeHandler = (e) => {
        setSelectedPartnerCurrency(e.value);
    };

    const viewModeChangeHandler = (ViewMode: number) => {
        return () => {
            setViewMode(ViewMode);
            dispatch(setUserDataApplicationModes({ Appearance: viewTheme, ViewMode }));
        };
    };

    const viewThemeChangeHandler = (Appearance: number) => {
        return () => {
            setViewTheme(Appearance);
            dispatch(setUserDataApplicationModes({ ViewMode: viewMode, Appearance }));
        };
    };

    const onCLoseModalHandler = () => {
        dispatch(setUserDataApplicationModes({ ...initialModeValues.current }));
        closeModal(false);
    };

    const onSaveHandler = () => {
        setIsSaveButtonLoading(true);

        const { name, events } = GAUserSettingsCategory;

        if (isPartnerCurrencyChanged) {
            logEvent(name, events.changeCurrency, selectedPartnerCurrency ?? l.SyncWithPartnerCurrency);
        }

        if (isViewThemeChanged) {
            logEvent(name, events.changeAppearance, Appearance[viewTheme]);
        }

        if (isViewModeChanged) {
            logEvent(name, events.changeViewMode, ViewMode[viewMode]);
        }

        saveHandler(
            {
                Currency: selectedPartnerCurrency,
                Appearance: viewTheme,
                ViewMode: viewMode,
            },
            !isPartnerCurrencyChanged,
        ).then(() => {
            setIsSaveButtonLoading(false);
            closeModal(false);
        });
    };

    const partnerCurrenciesDropdownData = useMemo(() => {
        return [
            { label: `${t(l.SyncWithPartnerCurrency)} (${ActivePartner?.CurrencyId})`, value: null },
            ...partnerCurrencies,
        ];
    }, [ActivePartner, partnerCurrencies, t]);

    useEffect(() => {
        setIsLoading(true);
        AuthHttpService.getUserSettings()
            .then((data) => {
                const { Data, HasError, AlertMessage } = data as unknown as AnyObject;
                if (HasError) {
                    throw new Error(AlertMessage);
                }

                setSelectedPartnerCurrency(Data.Currency);
                setInitialPartnerCurrency(Data.Currency);
                setViewMode(+Data.ViewMode);
                setViewTheme(+Data.Appearance);
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.log(error);
            })
            .finally(() => setIsLoading(false));
    }, []);

    return (
        <Modal
            background="dark-background"
            position="top"
            title="Settings"
            className="crm-modal settings-modal-cont"
            appearance="compact"
            onCancel={onCLoseModalHandler}
            closable={false}
            closeOnClickOutside={true}
            visible={true}
            size="content-size"
            footer={
                <>
                    <Button appearance="minimal" onClick={onCLoseModalHandler}>
                        {t(l.Close)}
                    </Button>
                    <ButtonWithLoader
                        isLoading={isSaveButtonLoading}
                        onClick={onSaveHandler}
                        disabled={!(isPartnerCurrencyChanged || isViewModeChanged || isViewThemeChanged)}
                    >
                        {t(l.Save)}
                    </ButtonWithLoader>
                </>
            }
        >
            <div className="settings-popup-container">
                <BusyLoader isBusy={isLoading}>
                    <div className="settings-popup-c">
                        <Dropdown
                            ref={partnerCurrencyRef}
                            labelAppearance="swap"
                            isMultiSelect={false}
                            disabled={false}
                            hasSearch={true}
                            onChange={partnerCurrencyChangeHandler}
                            value={selectedPartnerCurrency}
                            inputSize="big"
                            placeholder={t(l.CurrencyForReports)}
                            appearance="outline"
                            data={partnerCurrenciesDropdownData}
                            noDataText={t(l.NoDataFound)}
                        />
                    </div>

                    <div className="title-wrapper">
                        <p className="title-wrapper-text">{t(l.Appearance)}</p>
                        <span className="title-wrapper-line" />
                    </div>

                    <div className="settings-popup-mode settings-el-layout">
                        <button
                            className="settings-popup-radio-button"
                            onClick={viewThemeChangeHandler(Appearance.Light)}
                        >
                            <img alt="" src={lightModeImg} />
                            <div className="settings-popup-radio-button-title">
                                <Radio checked={viewTheme === Appearance.Light} />
                                <p>{t(l.Light)}</p>
                            </div>
                        </button>
                        <button
                            className="settings-popup-radio-button"
                            onClick={viewThemeChangeHandler(Appearance.Dark)}
                        >
                            <img alt="" src={darkModeImg} />
                            <div className="settings-popup-radio-button-title">
                                <Radio checked={viewTheme === Appearance.Dark} />
                                <p>{t(l.Dark)}</p>
                            </div>
                        </button>
                    </div>

                    <div className="title-wrapper">
                        <p className="title-wrapper-text">{t(l.ViewMode)}</p>
                        <span className="title-wrapper-line" />
                    </div>

                    <div className="settings-popup-view settings-el-layout">
                        <button className="settings-popup-radio-button" onClick={viewModeChangeHandler(ViewMode.Small)}>
                            <span className="view-mode-item view-mode-item-1">Aa</span>
                            <div className="settings-popup-radio-button-title">
                                <Radio checked={viewMode === ViewMode.Small} />
                                <p>{t(l.Small)}</p>
                            </div>
                        </button>
                        <button
                            className="settings-popup-radio-button"
                            onClick={viewModeChangeHandler(ViewMode.Normal)}
                        >
                            <span className="view-mode-item view-mode-item-2">Aa</span>
                            <div className="settings-popup-radio-button-title">
                                <Radio checked={viewMode === ViewMode.Normal} />
                                <p>{t(l.Normal)}</p>
                            </div>
                        </button>
                        <button className="settings-popup-radio-button" onClick={viewModeChangeHandler(ViewMode.Large)}>
                            <span className="view-mode-item view-mode-item-3">Aa</span>
                            <div className="settings-popup-radio-button-title">
                                <Radio checked={viewMode === ViewMode.Large} />
                                <p>{t(l.Large)}</p>
                            </div>
                        </button>
                    </div>
                </BusyLoader>
            </div>
        </Modal>
    );
};

UserProfileSettingsModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    saveHandler: PropTypes.func.isRequired,
};

UserProfileSettingsModal.displayName = 'UserProfileSettingsModal';

export default UserProfileSettingsModal;
